import {SpotlightSection_HighTierCouponElement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {useHistory} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  RotatingText,
  SlideAmbientProps,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, useEffect, useMemo} from 'react';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {useSplitFirstProductIds} from '../../hooks/useSplitFirstProductIds';
import SpotlightCouponBackground from '../assets/spotlight-coupon-background.svg';
import {imageCardStyles, StyledCountdownChip} from './Shared';
import {SlidePropsType} from './types';

export const HighTierCouponSlide: FC<
  {
    highTierCoupon: SpotlightSection_HighTierCouponElement;
  } & SlidePropsType & {SlideUIComponent: FC<SlideAmbientProps>}
> = ({
  highTierCoupon,
  isActive,
  isCloseToViewport,
  onSlideClick,
  'data-testid': dataTestid,
  SlideUIComponent,
  setNavBorderColor,
  uniqueKey = '',
  ...rest
}) => {
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();
  const history = useHistory();

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    highTierCoupon.linkTarget.url,
  );

  const onBannerTargetClick = () => {
    if (fullLink) {
      history.push(fullLink);
    }
  };

  const productImages = useMemo(
    () =>
      highTierCoupon.productGallery.map(product => ({
        url: product.image.url,
        description: product.image.description,
      })),
    [highTierCoupon.productGallery],
  );

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setNavBorderColor?.(isActive ? COLORS['Coupon.Primary'] : undefined);
  }, [isActive]);

  return (
    <SlideUIComponent
      {...rest}
      uniqueKey={uniqueKey}
      data-testid={dataTestid}
      bannerTarget={{
        url: linkWithoutFirstProductIds || highTierCoupon.linkTarget.url,
        external: highTierCoupon.linkTarget.external,
        onClick: onBannerTargetClick,
      }}
      headline={highTierCoupon.headline}
      subline={highTierCoupon.topline}
      productImages={productImages}
      isActive={Boolean(isActive)}
      isCloseToViewport={Boolean(isCloseToViewport)}
      onSlideClick={onSlideClick}
      floatingChip={
        disableUrgencyTriggers ? null : (
          <StyledCountdownChip
            expiryDate={highTierCoupon.endsAt}
            color="Coupon.Primary"
          />
        )
      }
      customSlideImage={
        <CouponImage $backgroundColor={COLORS['Coupon.Quaternary']} />
      }
      slideContentLabels={
        <CouponSlideContentWrapper>
          <CouponDiscount weight="w500" color="Coupon.Primary">
            {highTierCoupon.couponDiscount}
          </CouponDiscount>
          <StyledRotatingText
            color="Coupon.Primary"
            size="px18"
            interval={2800}
            labels={[
              highTierCoupon.couponText,
              highTierCoupon.couponSubline || '',
            ]}
          />
        </CouponSlideContentWrapper>
      }
    />
  );
};

const StyledRotatingText = styled(RotatingText)`
  > span {
    font-weight: ${FONT_WEIGHTS.w400};

    ${tabletAndUpLinaria(`
      font-size: ${FONT_SIZES.px32};
      font-weight: ${FONT_WEIGHTS.w500};
      line-height: 1.25;
    `)}
  }
`;

const CouponSlideContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 28px;
`;

const CouponImage = styled.div<{$backgroundColor: string}>`
  ${imageCardStyles}

  background-repeat: no-repeat;
  background-size: cover;
  background-color: ${props => props.$backgroundColor};
  background-image: radial-gradient(
      102.71% 47.36% at 2.45% 98.01%,
      rgba(21, 57, 207, 0.2) 0%,
      rgba(21, 57, 207, 0) 100%
    ),
    radial-gradient(
      121.8% 76.15% at 96.02% 0%,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0) 100%
    ),
    url(${SpotlightCouponBackground});

  ${tabletAndUpLinaria(`
    box-shadow: unset;
    aspect-ratio: initial;
    width: 100%;
  `)}
`;

const CouponDiscount = styled(Text)`
  font-size: 65px;

  ${tabletAndUpLinaria(`
    font-size: clamp(65px, 8vw, 120px);
    line-height: 1;
  `)}
`;
